import {BasicActivityTypesEnum} from '../../models/api/activityModel';
import {RulesNamespaceTypesEnum} from '../../models/api/ruleModel';
import {RuleActionTypesEnum} from './ruleActionModel';
import {RuleConditionTypesEnum} from './ruleConditionModel';
import {RuleEventTypesEnum} from './ruleEventModel';
import {RuleTriggerEventTypes} from './ruleTriggerModel';

interface AdvancedRules {
  [RulesNamespaceTypesEnum.EVENTS]: ReadonlyArray<RuleTriggerEventTypes>;
  [RulesNamespaceTypesEnum.ACTIONS]: ReadonlyArray<RuleActionTypesEnum>;
  [RulesNamespaceTypesEnum.COMPOUND_CONDITIONS]: ReadonlyArray<RuleConditionTypesEnum>;
}

export const advancedRules: AdvancedRules = {
  [RulesNamespaceTypesEnum.EVENTS]: [
    RuleActionTypesEnum.UNASSIGN,
    RuleEventTypesEnum.REOPEN,
    /** Below events are considered advanced following the updates to company plans in 2021. */
    RuleEventTypesEnum.COMMENT,
    RuleEventTypesEnum.MENTION,
    BasicActivityTypesEnum.REMINDER,
    BasicActivityTypesEnum.TRACKER_REMINDER,
    RuleActionTypesEnum.ASSIGN,
    RuleActionTypesEnum.ARCHIVE,
    RuleActionTypesEnum.TRASH,
    RuleEventTypesEnum.SNOOZE,
    BasicActivityTypesEnum.RESTORE,
    RuleActionTypesEnum.UNTAG,
    RuleActionTypesEnum.UNTAG_PRIVATE,
    RuleActionTypesEnum.MOVE,
    BasicActivityTypesEnum.SENDING_ERROR,
  ],
  [RulesNamespaceTypesEnum.ACTIONS]: [
    RuleActionTypesEnum.RANDOM_ASSIGN,
    RuleActionTypesEnum.ASSIGN_CONTACT_FIELD,
    RuleActionTypesEnum.ASSIGN_ACCOUNT_FIELD,
    RuleActionTypesEnum.NOTIFY_ASSIGNEE,
    RuleActionTypesEnum.NOTIFY_ACCOUNT_FIELD,
    /** Below actions are considered advanced following the updates to company plans in 2021. */
    RuleActionTypesEnum.FORWARD,
    RuleActionTypesEnum.ASSIGN,
    RuleActionTypesEnum.UNASSIGN,
    RuleActionTypesEnum.UNARCHIVE,
    RuleActionTypesEnum.TRASH,
    RuleActionTypesEnum.ADD_CONTACT_TO_GROUP,
    RuleActionTypesEnum.REMOVE_CONTACT_FROM_GROUP,
    RuleActionTypesEnum.ADD_COMMENT,
    RuleActionTypesEnum.ADD_EXPRESSIONS_LOG_COMMENT,
    RuleActionTypesEnum.AUTOLINK,
  ],
  [RulesNamespaceTypesEnum.COMPOUND_CONDITIONS]: [
    RuleConditionTypesEnum.UNREPLIED_AFTER,
    RuleConditionTypesEnum.UNASSIGNED_AFTER,
    RuleConditionTypesEnum.DELAY_SINCE_TRIGGER,
    RuleConditionTypesEnum.DAY_IS,
    RuleConditionTypesEnum.TIME_IS,
    RuleConditionTypesEnum.DATE_IS,
    RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_STRING,
    RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_NUMBER,
    RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_BOOLEAN,
    RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_DATETIME,
    RuleConditionTypesEnum.CUSTOM_ATTRIBUTE_TEAMMATE,
    RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_STRING,
    RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_NUMBER,
    RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_BOOLEAN,
    RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_DATETIME,
    RuleConditionTypesEnum.ACCOUNT_CUSTOM_ATTRIBUTE_TEAMMATE,
    RuleConditionTypesEnum.DURING_BUSINESS_HOURS,
    RuleConditionTypesEnum.OUTSIDE_BUSINESS_HOURS,
    RuleConditionTypesEnum.ATTACHMENT_NAME_CONTAINS,
    RuleConditionTypesEnum.ATTACHMENT_EXTENSION_IS,
    /** Below conditions are considered advanced following the updates to company plans in 2021. */
    RuleConditionTypesEnum.ALL_INDIVIDUAL_INBOXES,
    RuleConditionTypesEnum.ALL_SHARED_INBOXES,
    RuleConditionTypesEnum.CHANNEL_IN,
    RuleConditionTypesEnum.ASSIGNEE_IN,
    RuleConditionTypesEnum.ASSIGNEE_NOT_IN,
    RuleConditionTypesEnum.HAS_NO_TAGS,
    RuleConditionTypesEnum.HASNT_TAGS,
    RuleConditionTypesEnum.MESSAGE_HTML_BODY,
    RuleConditionTypesEnum.LANGUAGE,
    RuleConditionTypesEnum.MESSAGE_HEADERS,
    RuleConditionTypesEnum.HAS_ATTACHMENTS,
    RuleConditionTypesEnum.HAS_NO_ATTACHMENTS,
    RuleConditionTypesEnum.MESSAGE_TYPE,
    RuleConditionTypesEnum.MESSAGE_DIRECTION,
    RuleConditionTypesEnum.CONTACT_IN_GROUP,
    RuleConditionTypesEnum.CONTACT_NOT_IN_GROUP,
  ],
};

export const upgradedRules2021: AdvancedRules = {
  /**
   * These are rules which are now advanced, that were once basic. This list of rules is used
   * to grant older companies access to rules which have recently been added to advanced_rule.js.
   * These events, actions, and compoundConditions should not overlap with the items from advanced_rule.js
   */
  [RulesNamespaceTypesEnum.EVENTS]: [
    RuleEventTypesEnum.COMMENT,
    RuleEventTypesEnum.MENTION,
    BasicActivityTypesEnum.REMINDER,
    BasicActivityTypesEnum.TRACKER_REMINDER,
    RuleActionTypesEnum.ASSIGN,
    RuleActionTypesEnum.ARCHIVE,
    RuleActionTypesEnum.TRASH,
    BasicActivityTypesEnum.RESTORE,
    RuleActionTypesEnum.UNTAG,
    RuleActionTypesEnum.UNTAG_PRIVATE,
    RuleActionTypesEnum.MOVE,
    BasicActivityTypesEnum.SENDING_ERROR,
  ],
  [RulesNamespaceTypesEnum.ACTIONS]: [
    RuleActionTypesEnum.FORWARD,
    RuleActionTypesEnum.ASSIGN,
    RuleActionTypesEnum.UNASSIGN,
    RuleActionTypesEnum.UNARCHIVE,
    RuleActionTypesEnum.TRASH,
    RuleActionTypesEnum.ADD_CONTACT_TO_GROUP,
    RuleActionTypesEnum.REMOVE_CONTACT_FROM_GROUP,
  ],
  [RulesNamespaceTypesEnum.COMPOUND_CONDITIONS]: [
    RuleConditionTypesEnum.ALL_INDIVIDUAL_INBOXES,
    RuleConditionTypesEnum.ALL_SHARED_INBOXES,
    RuleConditionTypesEnum.CHANNEL_IN,
    RuleConditionTypesEnum.ASSIGNEE_IN,
    RuleConditionTypesEnum.ASSIGNEE_NOT_IN,
    RuleConditionTypesEnum.HAS_NO_TAGS,
    RuleConditionTypesEnum.HASNT_TAGS,
    RuleConditionTypesEnum.MESSAGE_HTML_BODY,
    RuleConditionTypesEnum.LANGUAGE,
    RuleConditionTypesEnum.MESSAGE_HEADERS,
    RuleConditionTypesEnum.HAS_ATTACHMENTS,
    RuleConditionTypesEnum.HAS_NO_ATTACHMENTS,
    RuleConditionTypesEnum.MESSAGE_TYPE,
    RuleConditionTypesEnum.MESSAGE_DIRECTION,
    RuleConditionTypesEnum.CONTACT_IN_GROUP,
    RuleConditionTypesEnum.CONTACT_NOT_IN_GROUP,
  ],
};
